// src/i18n.ts — Chargement dynamique des traductions (une seule langue à la fois)
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Seul le français (fallback) est chargé statiquement pour le premier rendu
import translationFR from '../translations/fr.json';

// Les autres langues sont chargées à la demande via dynamic import
const lazyResources: Record<string, () => Promise<{ default: Record<string, string> }>> = {
  en: () => import('../translations/en.json'),
  es: () => import('../translations/es.json'),
  pt: () => import('../translations/pt.json'),
};

const loadLanguage = async (lng: string) => {
  if (lng === 'fr' || i18n.hasResourceBundle(lng, 'translation')) return;
  const loader = lazyResources[lng];
  if (loader) {
    const mod = await loader();
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: { translation: translationFR },
    },
    fallbackLng: 'fr',
    interpolation: {
      escapeValue: false
    },
    // Forcer l'utilisation de codes de langue courts (fr au lieu de fr-FR)
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

// Charger la langue détectée si ce n'est pas le français
const detectedLng = i18n.language?.split('-')[0];
if (detectedLng && detectedLng !== 'fr') {
  loadLanguage(detectedLng);
}

// SEO meta descriptions per language
const META_DESCRIPTIONS: Record<string, string> = {
  fr: 'QuizVS : jeu de quiz multijoueur gratuit. Affrontez vos amis en temps réel ! SmartClash 1v1, ColorClash par équipes, Daily Streak, Weekly Challenge. 30+ catégories.',
  en: 'QuizVS: free multiplayer quiz game. Challenge friends in real-time! SmartClash 1v1, ColorClash team battles, Daily Streak, Weekly Challenge. 30+ categories.',
  es: 'QuizVS: juego de quiz multijugador gratuito. ¡Desafía a tus amigos en tiempo real! SmartClash 1v1, ColorClash por equipos, Daily Streak, Weekly Challenge. 30+ categorías.',
  pt: 'QuizVS: jogo de quiz multijogador gratuito. Desafie seus amigos em tempo real! SmartClash 1v1, ColorClash por equipes, Daily Streak, Weekly Challenge. 30+ categorias.',
};

// Update HTML lang and meta description on language change
function updateHtmlLang(lng: string) {
  document.documentElement.lang = lng;
  const meta = document.querySelector('meta[name="description"]');
  if (meta) meta.setAttribute('content', META_DESCRIPTIONS[lng] || META_DESCRIPTIONS.fr);
}

// Charger dynamiquement lors d'un changement de langue
i18n.on('languageChanged', (lng) => {
  loadLanguage(lng);
  updateHtmlLang(lng);
});

// Set initial lang
updateHtmlLang(detectedLng || 'fr');

export default i18n;